import React from 'react';
import {
  Badge, Box, Card, CardActions, CardMedia, Divider, IconButton, Typography,
} from '@mui/material';
import { useMutation, gql } from '@apollo/client';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { PetPhoto } from '../../types/PetPhoto';
import { formatRelativeTime } from '../../utilities';
import { LIKE_ANIMAL_MEDIA } from '../../queries';
import { photoOriginLabel, photoPetLabel } from './petLabel';

interface PhotoCardProps {
  photo: PetPhoto;
  onImageClick?: () => void;
}

function PhotoCard({ photo, onImageClick }: PhotoCardProps) {
  // Read directly from props (which come from Apollo cache)
  const isLiked = photo.isLikedByCurrentUser ?? false;
  const likeCount = photo.likes ?? 0;

  const [likeAnimalMedia, { loading: isLoading }] = useMutation(LIKE_ANIMAL_MEDIA, {
    update: (cache, { data }) => {
      if (!data?.likeAnimalMedia) return;

      // Update the cached PetPhoto directly - this updates ALL queries containing this photo
      cache.writeFragment({
        id: cache.identify({ __typename: 'PetPhoto', id: photo.id }),
        fragment: gql`
          fragment UpdatedPetPhoto on PetPhoto {
            likes
            isLikedByCurrentUser
          }
        `,
        data: {
          likes: data.likeAnimalMedia.likes,
          isLikedByCurrentUser: data.likeAnimalMedia.isLikedByCurrentUser,
        },
      });
    },
    onError: (error) => {
      console.error('Failed to like photo:', error);
    },
  });

  const handleLike = () => {
    if (isLoading) return;

    const newLikedState = !isLiked;

    likeAnimalMedia({
      variables: {
        animalMediaId: parseInt(photo.id, 10),
        liked: newLikedState,
      },
      // Optimistic response updates cache immediately
      optimisticResponse: {
        likeAnimalMedia: {
          __typename: 'LikeAnimalMediaResponse',
          success: true,
          likes: newLikedState ? likeCount + 1 : likeCount - 1,
          isLikedByCurrentUser: newLikedState,
        },
      },
    });
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'none',
        border: '1px solid #E0E0E0',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Badge
          badgeContent={formatRelativeTime(photo.createdAt)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            '& .MuiBadge-badge': {
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 500,
              minWidth: 'auto',
              padding: '4px 8px',
              borderRadius: '12px',
              position: 'static',
              transform: 'none',
            },
          }}
        />
        <CardMedia
          component="img"
          image={photo.url}
          alt={photoPetLabel(photo, 'Pet photo')}
          onClick={onImageClick}
          sx={{
            aspectRatio: '1/1',
            objectFit: 'cover',
            backgroundColor: '#eee',
            cursor: onImageClick ? 'pointer' : 'default',
          }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}
              component="div"
              noWrap
            >
              {photoPetLabel(photo)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              {photoOriginLabel(photo)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />
      <CardActions disableSpacing sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          aria-label={isLiked ? 'unlike photo' : 'like photo'}
          onClick={handleLike}
          size="small"
          sx={{
            ml: 0.5,
            color: isLiked ? 'error.main' : 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          {isLiked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
        </IconButton>
        {
          likeCount > 0 && (
            <Typography
              variant="body2"
              sx={{
              fontSize: 12,
              color: 'text.secondary',
              ml: 0.5,
            }}
            >
              {likeCount}
            </Typography>
          )
        }
      </CardActions>
    </Card>
  );
}

export default PhotoCard;
